import {
  ArrowPathIcon,
  BellIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlayIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Collapse,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  rem,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { useNotificationRefresh } from "../../context/NotificationRefreshContext";
import {
  NotificationAdminService,
  NotificationConfiguration,
} from "../../services/admin";
import { CVClassificationService } from "../../services/cvClassificationService";
import { PersonBasicInfo, PersonService } from "../../services/personService";
import { Notification } from "../../types/notification";

const notificationAdminService = new NotificationAdminService();
const personService = new PersonService();
const cvClassificationService = new CVClassificationService();

interface NotificationResult {
  type: "success" | "error";
  message: string;
  notifications?: Notification[];
  timestamp: Date;
}

export function OperationsNotificationTesting() {
  // Configuration state
  const [configurations, setConfigurations] = useState<
    NotificationConfiguration[]
  >([]);
  const [configurationsLoading, setConfigurationsLoading] = useState(true);
  const [editingConfig, setEditingConfig] =
    useState<NotificationConfiguration | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [configurationTypeFilter, setConfigurationTypeFilter] =
    useState<string>("");

  // Testing state
  const [persons, setPersons] = useState<PersonBasicInfo[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [personsLoading, setPersonsLoading] = useState(true);
  const [results, setResults] = useState<NotificationResult[]>([]);

  // CV Classification state
  const [cvClassificationLoading, setCvClassificationLoading] = useState(false);

  // Test results state
  const [testResultsCollapsed, setTestResultsCollapsed] = useState(true);

  // Notification refresh context
  const { refreshNotifications } = useNotificationRefresh();

  // Load data on component mount
  useEffect(() => {
    loadConfigurations();
    loadPersons();
  }, []);

  const loadConfigurations = async () => {
    try {
      setConfigurationsLoading(true);
      const configsData = await notificationAdminService.getConfigurations(
        configurationTypeFilter || undefined
      );
      setConfigurations(configsData);
    } catch (error) {
      console.error("Error loading configurations:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load notification configurations",
        color: "red",
      });
    } finally {
      setConfigurationsLoading(false);
    }
  };

  const loadPersons = async () => {
    try {
      setPersonsLoading(true);
      const personsData = await personService.getBasicInfo();
      setPersons(personsData);
    } catch (error) {
      console.error("Error loading persons:", error);
      notifications.show({
        title: "Error",
        message: "Failed to load persons",
        color: "red",
      });
    } finally {
      setPersonsLoading(false);
    }
  };

  const handleEditConfiguration = (config: NotificationConfiguration) => {
    setEditingConfig(config);
    setEditValue(config.configurationValue);
    openEditModal();
  };

  const handleSaveConfiguration = async () => {
    if (!editingConfig) return;

    try {
      const updatedConfig = await notificationAdminService.updateConfiguration(
        editingConfig.id,
        editValue
      );

      // Update the configuration in the list
      setConfigurations((prev) =>
        prev.map((config) =>
          config.id === updatedConfig.id ? updatedConfig : config
        )
      );

      notifications.show({
        title: "Success",
        message: "Configuration updated successfully",
        color: "green",
      });

      closeEditModal();
    } catch (error) {
      console.error("Error updating configuration:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update configuration",
        color: "red",
      });
    }
  };

  // Get unique configuration types for filter
  const configurationTypes = Array.from(
    new Set(configurations.map((config) => config.configurationType))
  ).filter(Boolean);

  const addResult = (result: NotificationResult) => {
    setResults((prev) => [result, ...prev.slice(0, 9)]); // Keep only last 10 results
  };

  const handleTriggerCVClassification = async () => {
    setCvClassificationLoading(true);
    try {
      await cvClassificationService.triggerCVClassification();

      addResult({
        type: "success",
        message:
          "CV Classification function triggered successfully. The process is running in the background and may take several minutes to complete.",
        timestamp: new Date(),
      });

      notifications.show({
        title: "Success",
        message: "CV Classification started in background",
        color: "green",
      });
    } catch (error) {
      console.error("Error triggering CV classification:", error);
      addResult({
        type: "error",
        message: `Failed to trigger CV classification: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Error",
        message: "Failed to trigger CV classification",
        color: "red",
      });
    } finally {
      setCvClassificationLoading(false);
    }
  };

  const handleGenerateAllNotifications = async () => {
    setIsLoading(true);
    try {
      const generatedNotifications =
        await notificationAdminService.generateAllNotifications();

      addResult({
        type: "success",
        message: `Successfully generated ${generatedNotifications.length} notifications for all users`,
        notifications: generatedNotifications,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Success",
        message: `Generated ${generatedNotifications.length} notifications`,
        color: "green",
      });

      // Refresh notifications in floating chat
      notifications.show({
        title: "Refreshing",
        message: "Updating notification display...",
        color: "blue",
        autoClose: 2000,
      });
      await refreshNotifications();
    } catch (error) {
      console.error("Error generating all notifications:", error);
      addResult({
        type: "error",
        message: `Failed to generate notifications: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Error",
        message: "Failed to generate notifications",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateUserNotifications = async () => {
    if (!selectedPersonId) {
      notifications.show({
        title: "Error",
        message: "Please select a person first",
        color: "red",
      });
      return;
    }

    setIsLoading(true);
    try {
      const personId = parseInt(selectedPersonId);
      const generatedNotifications =
        await notificationAdminService.generateNotificationsForPerson(personId);

      const selectedPerson = persons.find((p) => p.id === personId);
      const personName = selectedPerson
        ? `${selectedPerson.firstName} ${selectedPerson.lastName}`
        : `Person ${personId}`;

      addResult({
        type: "success",
        message: `Successfully generated ${generatedNotifications.length} notifications for ${personName}`,
        notifications: generatedNotifications,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Success",
        message: `Generated ${generatedNotifications.length} notifications for ${personName}`,
        color: "green",
      });

      // Refresh notifications in floating chat
      notifications.show({
        title: "Refreshing",
        message: "Updating notification display...",
        color: "blue",
        autoClose: 2000,
      });
      await refreshNotifications();
    } catch (error) {
      console.error("Error generating user notifications:", error);
      addResult({
        type: "error",
        message: `Failed to generate notifications: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Error",
        message: "Failed to generate notifications",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateSystemNotifications = async () => {
    if (!selectedPersonId) {
      notifications.show({
        title: "Error",
        message: "Please select a person first",
        color: "red",
      });
      return;
    }

    setIsLoading(true);
    try {
      const personId = parseInt(selectedPersonId);
      const generatedNotifications =
        await notificationAdminService.generateSystemNotificationsForPerson(
          personId
        );

      const selectedPerson = persons.find((p) => p.id === personId);
      const personName = selectedPerson
        ? `${selectedPerson.firstName} ${selectedPerson.lastName}`
        : `Person ${personId}`;

      addResult({
        type: "success",
        message: `Successfully generated ${generatedNotifications.length} manager-level notifications for ${personName}`,
        notifications: generatedNotifications,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Success",
        message: `Generated ${generatedNotifications.length} manager-level notifications for ${personName}`,
        color: "green",
      });

      // Refresh notifications in floating chat
      notifications.show({
        title: "Refreshing",
        message: "Updating notification display...",
        color: "blue",
        autoClose: 2000,
      });
      await refreshNotifications();
    } catch (error) {
      console.error("Error generating manager notifications:", error);
      addResult({
        type: "error",
        message: `Failed to generate manager notifications: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        timestamp: new Date(),
      });

      notifications.show({
        title: "Error",
        message: "Failed to generate manager notifications",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString();
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case "cvnotupdated":
        return "orange";
      case "certificationexpiringsoon":
        return "red";
      case "onbenchupdatecv":
        return "blue";
      case "lowcvscore":
        return "yellow";
      case "peopleavailablesoon":
        return "green";
      default:
        return "gray";
    }
  };

  const renderNotificationCard = (notification: Notification) => {
    let content;
    try {
      content = JSON.parse(notification.content);
    } catch {
      content = { raw: notification.content };
    }

    return (
      <Card key={notification.id} p="sm" withBorder>
        <Group justify="space-between" mb="xs">
          <Badge color={getNotificationTypeColor(notification.type)} size="sm">
            {notification.type}
          </Badge>
          <Text size="xs" c="dimmed">
            ID: {notification.id}
          </Text>
        </Group>
        <Text size="sm" mb="xs">
          <strong>Person ID:</strong> {notification.personId}
        </Text>
        <Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>
          {JSON.stringify(content, null, 2)}
        </Text>
      </Card>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Notifications", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Notifications
          </Title>
          <Text c="dimmed" size="lg">
            Manage notification configurations and test notification generation
          </Text>
        </div>

        {/* Notification Configuration Section */}
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group justify="space-between">
              <Group>
                <Cog6ToothIcon style={{ width: rem(20), height: rem(20) }} />
                <Title order={2} size="h3">
                  Notification Configuration
                </Title>
              </Group>
              <Button
                leftSection={<ArrowPathIcon style={{ width: rem(16) }} />}
                onClick={loadConfigurations}
                variant="light"
                size="sm"
              >
                Refresh
              </Button>
            </Group>

            <Group>
              <Select
                placeholder="Filter by type"
                data={configurationTypes.map((type) => ({
                  value: type,
                  label: type,
                }))}
                value={configurationTypeFilter}
                onChange={(value) => {
                  setConfigurationTypeFilter(value || "");
                  // Reload configurations with new filter
                  if (value !== configurationTypeFilter) {
                    loadConfigurations();
                  }
                }}
                clearable
                style={{ minWidth: 200 }}
              />
            </Group>

            {configurationsLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Configuration Key</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {configurations.map((config) => (
                      <Table.Tr key={config.id}>
                        <Table.Td>
                          <Text fw={600}>{config.configurationKey}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {config.configurationValue}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {config.configurationType}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{config.description}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label="Edit Configuration">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => handleEditConfiguration(config)}
                            >
                              <PencilIcon style={{ width: rem(16) }} />
                            </ActionIcon>
                          </Tooltip>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Stack>
        </Paper>

        {/* CV Classification Section */}
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group>
              <CpuChipIcon style={{ width: rem(20), height: rem(20) }} />
              <Title order={2} size="h3">
                CV Classification
              </Title>
            </Group>

            <Card withBorder p="md">
              <Stack gap="md">
                <Group>
                  <CpuChipIcon style={{ width: rem(20), height: rem(20) }} />
                  <div>
                    <Text fw={600}>Run CV Classification</Text>
                    <Text size="sm" c="dimmed">
                      Triggers the Azure Function to analyze and score all CVs
                      in the database. This process runs in the background and
                      may take several minutes to complete.
                    </Text>
                  </div>
                </Group>
                <Button
                  leftSection={<PlayIcon style={{ width: rem(16) }} />}
                  onClick={handleTriggerCVClassification}
                  loading={cvClassificationLoading}
                  color="purple"
                >
                  {cvClassificationLoading
                    ? "Starting CV Classification..."
                    : "Start CV Classification"}
                </Button>
                <Alert
                  icon={<ExclamationTriangleIcon style={{ width: rem(16) }} />}
                  title="Background Process"
                  color="blue"
                  variant="light"
                >
                  This function runs asynchronously in the background. You don't
                  need to wait for it to complete. The process will analyze all
                  CVs and update their scores in the database.
                </Alert>
              </Stack>
            </Card>
          </Stack>
        </Paper>

        {/* Testing Section */}
        <Alert
          icon={<ExclamationTriangleIcon style={{ width: rem(16) }} />}
          title="Testing Environment Only"
          color="yellow"
        >
          The endpoints below are for testing and demo purposes only. In
          production, notifications are generated automatically by scheduled
          Azure functions.
        </Alert>

        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Group>
              <BellIcon style={{ width: rem(20), height: rem(20) }} />
              <Title order={2} size="h3">
                Notification Testing
              </Title>
            </Group>

            {/* Generate All Notifications */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Group>
                  <UsersIcon style={{ width: rem(20), height: rem(20) }} />
                  <div>
                    <Text fw={600}>Generate All Notifications</Text>
                    <Text size="sm" c="dimmed">
                      POST /notifications/generate - Generates notifications for
                      all users
                    </Text>
                  </div>
                </Group>
                <Button
                  leftSection={<PlayIcon style={{ width: rem(16) }} />}
                  onClick={handleGenerateAllNotifications}
                  loading={isLoading}
                  color="blue"
                >
                  Generate All Notifications
                </Button>
              </Stack>
            </Card>

            {/* User Selection */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Group>
                  <UserIcon style={{ width: rem(20), height: rem(20) }} />
                  <div>
                    <Text fw={600}>Generate User-Specific Notifications</Text>
                    <Text size="sm" c="dimmed">
                      Select a person to generate notifications for
                    </Text>
                  </div>
                </Group>

                {personsLoading ? (
                  <Center>
                    <Loader size="sm" />
                  </Center>
                ) : (
                  <Select
                    placeholder="Select a person"
                    data={persons.map((person) => ({
                      value: person.id.toString(),
                      label: `${person.firstName} ${person.lastName} (${person.email})`,
                    }))}
                    value={selectedPersonId}
                    onChange={(value) => setSelectedPersonId(value || "")}
                    searchable
                    clearable
                  />
                )}

                <Group grow>
                  <Button
                    leftSection={<PlayIcon style={{ width: rem(16) }} />}
                    onClick={handleGenerateUserNotifications}
                    loading={isLoading}
                    disabled={!selectedPersonId}
                    color="green"
                  >
                    Generate All Notifications
                  </Button>
                  <Button
                    leftSection={<PlayIcon style={{ width: rem(16) }} />}
                    onClick={handleGenerateSystemNotifications}
                    loading={isLoading}
                    disabled={!selectedPersonId}
                    color="orange"
                  >
                    Generate Manager Notifications
                  </Button>
                </Group>

                <Stack gap="xs">
                  <Text size="sm" c="dimmed">
                    <strong>Generate All Notifications:</strong> POST
                    /notifications/generate/user/&#123;userId&#125; - Generates
                    all applicable notifications for the selected person
                  </Text>
                  <Text size="sm" c="dimmed">
                    <strong>Generate Manager Notifications:</strong> POST
                    /notifications/generate/system-notifications/user/&#123;userId&#125;
                    - Generates notifications typically available to managers
                  </Text>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Paper>

        {/* Results Section */}
        {results.length > 0 && (
          <Paper p="xl" withBorder>
            <Stack gap="lg">
              <Group justify="space-between">
                <Button
                  variant="subtle"
                  leftSection={
                    testResultsCollapsed ? (
                      <ChevronRightIcon style={{ width: rem(16) }} />
                    ) : (
                      <ChevronDownIcon style={{ width: rem(16) }} />
                    )
                  }
                  onClick={() => setTestResultsCollapsed(!testResultsCollapsed)}
                  size="md"
                >
                  <Title order={2} size="h3">
                    Test Results ({results.length})
                  </Title>
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => setResults([])}
                >
                  Clear Results
                </Button>
              </Group>

              <Collapse in={!testResultsCollapsed}>
                <ScrollArea h={400}>
                  <Stack gap="md">
                    {results.map((result, index) => (
                      <Card key={index} withBorder p="md">
                        <Stack gap="sm">
                          <Group justify="space-between">
                            <Group>
                              {result.type === "success" ? (
                                <CheckCircleIcon
                                  style={{ width: rem(16), color: "green" }}
                                />
                              ) : (
                                <ExclamationTriangleIcon
                                  style={{ width: rem(16), color: "red" }}
                                />
                              )}
                              <Text
                                fw={600}
                                c={result.type === "success" ? "green" : "red"}
                              >
                                {result.type === "success"
                                  ? "Success"
                                  : "Error"}
                              </Text>
                            </Group>
                            <Group>
                              <ClockIcon style={{ width: rem(14) }} />
                              <Text size="xs" c="dimmed">
                                {formatTimestamp(result.timestamp)}
                              </Text>
                            </Group>
                          </Group>

                          <Text size="sm">{result.message}</Text>

                          {result.notifications &&
                            result.notifications.length > 0 && (
                              <Box>
                                <Divider my="sm" />
                                <Text size="sm" fw={600} mb="sm">
                                  Generated Notifications (
                                  {result.notifications.length}):
                                </Text>
                                <Stack gap="xs">
                                  {result.notifications
                                    .slice(0, 3)
                                    .map(renderNotificationCard)}
                                  {result.notifications.length > 3 && (
                                    <Text size="xs" c="dimmed" ta="center">
                                      ... and {result.notifications.length - 3}{" "}
                                      more
                                    </Text>
                                  )}
                                </Stack>
                              </Box>
                            )}
                        </Stack>
                      </Card>
                    ))}
                  </Stack>
                </ScrollArea>
              </Collapse>
            </Stack>
          </Paper>
        )}
      </Stack>

      {/* Edit Configuration Modal */}
      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title="Edit Configuration"
        size="md"
      >
        <Stack gap="md">
          {editingConfig && (
            <>
              <TextInput
                label="Configuration Key"
                value={editingConfig.configurationKey}
                disabled
              />
              <Textarea
                label="Configuration Value"
                value={editValue}
                onChange={(event) => setEditValue(event.currentTarget.value)}
                minRows={3}
              />
              <TextInput
                label="Type"
                value={editingConfig.configurationType}
                disabled
              />
              <Textarea
                label="Description"
                value={editingConfig.description}
                disabled
                minRows={2}
              />
              <Group justify="flex-end">
                <Button variant="light" onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button onClick={handleSaveConfiguration}>Save Changes</Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Container>
  );
}
